/**
 * Computes the SHA-256 hash of a given message string.
 * @param message - The input string to be hashed.
 * @returns A promise that resolves to the SHA-256 hash of the input message, represented as a hexadecimal string.
 * @remarks
 * This function uses the Web Crypto API's `crypto.subtle.digest` method to perform the hashing.
 * If the `crypto.subtle.digest` method is not available, the function returns the original message.
 * @example
 * ```typescript
 * const hash = await sha256('hello world');
 * console.log(hash); // Outputs the SHA-256 hash of 'hello world'
 * ```
 */
export async function sha256(message: string) {
  if (!crypto.subtle?.digest) return message;

  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(message);

  // hash the message
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  // convert bytes to hex string
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
}
